// src/components/financials/CashFlowSimple.jsx
// SCREENER-FINANCIALS — new file. "Simple Mode" for Cash Flow: three plain-
// English labeled flows, each shown as a bar growing left (outflow, red) or
// right (inflow, green) from a center zero-line — visually answers "where
// did the cash come from, where did it go" without requiring the reader to
// already know what "financing activities" means as an accounting term.

import React from 'react';

function fmtCompact(n) {
  if (n == null) return '—';
  const abs = Math.abs(Number(n));
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e9) return sign + '$' + (abs / 1e9).toFixed(2) + 'B';
  if (abs >= 1e6) return sign + '$' + (abs / 1e6).toFixed(2) + 'M';
  if (abs >= 1e3) return sign + '$' + (abs / 1e3).toFixed(1) + 'K';
  return sign + '$' + abs.toFixed(0);
}

function FlowBar({ icon, label, value, maxAbs }) {
  if (value == null) {
    return (
      <div style={{ marginBottom: '0.75rem', fontSize: '0.78rem', color: '#475569' }}>
        {icon} {label}: not captured for this period
      </div>
    );
  }
  const pct = maxAbs > 0 ? Math.min(100, (Math.abs(value) / maxAbs) * 100) : 0;
  const positive = value >= 0;
  return (
    <div style={{ marginBottom: '0.9rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
        <span style={{ fontSize: '0.8rem', color: '#cbd5e1' }}>{icon} {label}</span>
        <span style={{ fontSize: '0.85rem', fontWeight: 700, color: positive ? '#00ff88' : '#ff4444' }}>
          {positive ? '+' : ''}{fmtCompact(value)}
        </span>
      </div>
      <div style={{ height: '10px', background: 'rgba(255,255,255,0.05)', borderRadius: '6px',
        position: 'relative', display: 'flex', alignItems: 'center' }}>
        <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: '1px', background: 'rgba(255,255,255,0.15)' }} />
        {positive ? (
          <div style={{ position: 'absolute', left: '50%', width: (pct / 2) + '%', height: '100%',
            background: '#00ff88', borderRadius: '0 6px 6px 0' }} />
        ) : (
          <div style={{ position: 'absolute', right: '50%', width: (pct / 2) + '%', height: '100%',
            background: '#ff4444', borderRadius: '6px 0 0 6px' }} />
        )}
      </div>
    </div>
  );
}

export default function CashFlowSimple({ statements, computed }) {
  const s = statements;
  const c = computed || {};
  const values = [s.operatingCashFlow, s.investingCashFlow, s.financingCashFlow].filter(v => v != null).map(Math.abs);
  const maxAbs = values.length ? Math.max(...values) : 0;

  const netChange = [s.operatingCashFlow, s.investingCashFlow, s.financingCashFlow]
    .every(v => v != null)
    ? s.operatingCashFlow + s.investingCashFlow + s.financingCashFlow
    : null;

  return (
    <div>
      <FlowBar icon="⚙️" label="Cash From Running the Business" value={s.operatingCashFlow} maxAbs={maxAbs} />
      <FlowBar icon="🏗️" label="Cash Spent on Growth & Investments" value={s.investingCashFlow} maxAbs={maxAbs} />
      <FlowBar icon="🏦" label="Cash From Loans, Stock & Dividends" value={s.financingCashFlow} maxAbs={maxAbs} />

      <div style={{ marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid rgba(255,255,255,0.06)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
          {netChange != null ? (netChange >= 0 ? '📈 Cash increased this period' : '📉 Cash decreased this period') : '💵 Free Cash Flow'}
        </span>
        <span style={{ fontSize: '0.9rem', fontWeight: 800, color: (netChange != null ? netChange : c.freeCashFlow) >= 0 ? '#00ff88' : '#ff4444' }}>
          {fmtCompact(netChange != null ? netChange : c.freeCashFlow)}
        </span>
      </div>
    </div>
  );
}
